import React, { useEffect, useState } from "react";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { api, useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Clock, MapPin, User } from "lucide-react";
import { ContentSyncCard } from "@/admin/ContentSyncCard";

const Card = ({ label, value, tint }) => (
  <div className="card-surface p-6">
    <div className="eyebrow mb-2">{label}</div>
    <div className={`font-display text-5xl ${tint || ""}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [today, setToday] = useState(null);
  const [assign, setAssign] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    api.get("/admin/dashboard").then(r => setD(r.data)).catch(() => {});
    api.get("/admin/my-classes-today").then(r => setToday(r.data)).catch(() => {});
    api.get("/admin/my-assignments").then(r => setAssign(r.data)).catch(() => {});
  }, []);
  if (!d && !today) return <div className="p-4 sm:p-8 text-zinc-500">Loading…</div>;
  const isInstructor = user?.role === "instructor";

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">{isInstructor ? "Instructor view" : "Overview"}</div>
          <h1 className="font-display text-5xl uppercase">{isInstructor ? `Welcome, ${user?.instructor_name || user?.name || "Coach"}` : "Dashboard"}</h1>
        </div>
        {!isInstructor && (
          <button onClick={async () => {
            try {
              const r = await api.get("/admin/backup/export.zip", { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url;
              a.download = `2rightfeet-backup-${new Date().toISOString().slice(0,10)}.zip`;
              document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
              toast.success("Backup downloaded");
            } catch (e) { toast.error(e.response?.data?.detail || "Backup failed — super admin only"); }
          }} className="btn-red flex items-center gap-2" data-testid="download-backup"><Download size={14}/>Download backup</button>
        )}
      </div>

      {user?.role === "super_admin" && <div className="mb-8"><ContentSyncCard /></div>}

      {/* My assignments (scoped per user) */}
      {assign && (assign.sessions.length > 0 || assign.workshops.length > 0) && (
        <div className="card-surface p-6 mb-8" data-testid="my-assignments-card">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="eyebrow">My assignments</div>
              <div className="text-zinc-500 text-xs mt-1">Everything you're leading — classes + workshops</div>
            </div>
            <Link to="/admin/availability" className="text-xs text-red-500 uppercase tracking-widest">My availability →</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {assign.sessions.map(s => (
              <div key={s.id} className="border border-white/10 p-4 bg-black/40 text-sm" data-testid={`assign-session-${s.id}`}>
                <div className="font-display text-lg uppercase leading-tight">{s.batch_name}</div>
                <div className="text-zinc-400 mt-1 flex items-center gap-2"><Clock size={12}/>{s.day} · {fmtRange(s.start_time, s.end_time)}</div>
                <div className="text-zinc-400 flex items-center gap-2"><MapPin size={12}/>{s.location_name || "—"}</div>
              </div>
            ))}
            {assign.workshops.map(w => (
              <div key={w.id} className="border border-amber-500/40 p-4 bg-amber-500/5 text-sm" data-testid={`assign-workshop-${w.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Workshop</div>
                <div className="font-display text-lg uppercase leading-tight mt-1">{w.name}</div>
                <div className="text-zinc-400 mt-1 flex items-center gap-2"><Clock size={12}/>{w.date} {fmtTime(w.start_time)}</div>
                <div className="text-zinc-400 flex items-center gap-2"><MapPin size={12}/>{w.location_name || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My classes today (shown to everyone; scoped by role on backend) */}
      {today && (
        <div className="card-surface p-6 mb-8" data-testid="today-classes-card">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="eyebrow">{isInstructor ? "My classes today" : "Classes today"}</div>
              <div className="text-zinc-500 text-xs mt-1">{today.today}</div>
            </div>
            <Link to="/admin/sessions" className="text-xs text-red-500 uppercase tracking-widest">Manage sessions →</Link>
          </div>
          {today.sessions.length === 0 ? (
            <div className="text-zinc-500 text-sm">No classes scheduled today.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {today.sessions.map(s => (
                <div key={s.id} className="border border-white/10 p-4 bg-black/40" data-testid={`today-session-${s.id}`}>
                  <div className="font-display text-xl uppercase leading-tight">{s.batch_name || "Session"}</div>
                  <div className="text-zinc-400 text-sm mt-2 flex items-center gap-2"><Clock size={12}/>{fmtRange(s.start_time, s.end_time)}</div>
                  <div className="text-zinc-400 text-sm mt-1 flex items-center gap-2"><MapPin size={12}/>{s.location_name || "—"}</div>
                  {s.instructor && <div className="text-zinc-400 text-sm mt-1 flex items-center gap-2"><User size={12}/>{s.instructor}</div>}
                  <div className={`text-xs uppercase tracking-widest mt-3 ${s.spots_left <= 0 ? "text-red-500" : s.spots_left <= 3 ? "text-amber-400" : "text-zinc-500"}`}>
                    {s.registration_count || 0}/{s.capacity || 0} enrolled · {s.spots_left || 0} spots left
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!isInstructor && d && (<>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Card label="Inquiries" value={d.stats.total_inquiries} />
        <Card label="New Inquiries" value={d.stats.new_inquiries} tint="text-red-500" />
        <Card label="Registrations" value={d.stats.total_registrations} />
        <Card label="Upcoming Workshops" value={d.stats.upcoming_workshops} />
        <Card label="Workshop Regs" value={d.stats.workshop_registrations} />
        <Card label="Pre-Registrations" value={d.stats.pre_registrations} />
        <Card label="Comp Applications" value={d.stats.competition_applications} tint={d.stats.competition_applications > 0 ? "text-red-500" : ""} />
      </div>

      {d.stats.almost_full_sessions > 0 && (
        <div className="card-surface p-6 mb-8 border-l-4 border-amber-400">
          <div className="eyebrow text-amber-400 mb-1">Alert</div>
          <div>{d.stats.almost_full_sessions} class {d.stats.almost_full_sessions === 1 ? "session is" : "sessions are"} almost full.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="eyebrow">Recent inquiries</div>
            <Link to="/admin/inquiries" className="text-xs text-red-500 uppercase tracking-widest">View all →</Link>
          </div>
          {d.recent_inquiries.length === 0 && <div className="text-zinc-500 text-sm">None yet.</div>}
          <div className="divide-y divide-white/5">
            {d.recent_inquiries.map(i => (
              <div key={i.id} className="py-3">
                <div className="font-semibold">{i.name}</div>
                <div className="text-zinc-400 text-sm line-clamp-1">{i.message}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card-surface p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="eyebrow">Recent registrations</div>
            <Link to="/admin/registrations" className="text-xs text-red-500 uppercase tracking-widest">View all →</Link>
          </div>
          {d.recent_registrations.length === 0 && <div className="text-zinc-500 text-sm">None yet.</div>}
          <div className="divide-y divide-white/5">
            {d.recent_registrations.map(r => (
              <div key={r.id} className="py-3">
                <div className="font-semibold">{r.full_name}</div>
                <div className="text-zinc-400 text-sm">{r.email} · {r.session_snapshot?.day} {fmtTime(r.session_snapshot?.start_time)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
